import React, { Fragment, ReactNode, useContext } from 'react';

import { FormattedMessage } from 'react-intl';
import { useSelector } from 'react-redux';
import { Link } from 'react-resource-router';

import Lozenge from '@atlaskit/lozenge';

import { CommitEntryHashItem } from 'src/components/activity/types';
import { useConversationsContext } from 'src/contexts/conversations-context';
import { getRepoCommitsState } from 'src/redux/repo-commits/selectors';
import { useParams } from 'src/router/hooks';
import urls from 'src/urls';
import { hashCompare } from 'src/utils/short-hash';

import {
  DiffStateContext,
  SelectedDiffType,
} from '../tabs/diff/diff-state-context';

import { usePublishActivityUiEvent } from './hooks';
import { messages } from './pull-request-activity-events.i18n';
import * as styles from './pull-request-event.styled';

type Props = {
  hashes: CommitEntryHashItem[];
};

export const PullRequestCommitEntry = ({ hashes }: Props): JSX.Element => {
  const { activityFeedVersion } = useConversationsContext();
  const { setIterativeReviewSelectedDiff } = useContext(DiffStateContext);
  const publish = usePublishActivityUiEvent(
    'commit-update',
    activityFeedVersion || ''
  );
  const { repositoryOwner, repositorySlug, pullRequestId } = useParams<{
    repositoryOwner: string;
    repositorySlug: string;
    pullRequestId: string;
  }>();

  const { commits } = useSelector(getRepoCommitsState);

  return (
    <Fragment>
      <Lozenge appearance="moved">
        <FormattedMessage {...messages.updatedEventLozenge} />
      </Lozenge>
      <styles.MessageText>
        <FormattedMessage
          {...messages.commitEventMessage}
          values={{ numOfCommits: hashes.length }}
        />
        :
      </styles.MessageText>{' '}
      <styles.CommitText>
        {hashes
          .map<ReactNode>(hashItem => {
            if (!!hashItem.hash && !!hashItem.parentHash) {
              const found = commits?.find(c =>
                hashCompare(c.hash, hashItem.hash || '')
              );
              if (!found) {
                const commitPageRedirect = `${urls.ui.fullCommit(
                  `${repositoryOwner}/${repositorySlug}`,
                  hashItem.hash
                )}`;
                return (
                  <Link
                    key={`${hashItem.url}-${hashItem.hash}`}
                    to={commitPageRedirect}
                  >
                    {hashItem.hash}
                  </Link>
                );
              } else {
                const diffTabRedirect = `${urls.ui.pullRequestTab(
                  repositoryOwner,
                  repositorySlug,
                  pullRequestId,
                  'diff'
                )}/${hashItem.hash}..${hashItem.parentHash}`;
                const onClick = () => {
                  publish();
                  setIterativeReviewSelectedDiff({
                    sourceHash: hashItem.hash!,
                    destHash: hashItem.parentHash!,
                    selectedDiffType: SelectedDiffType.Single,
                    isLoading: false,
                  });
                };
                return (
                  <Link
                    key={`${hashItem.url}-${hashItem.hash}`}
                    to={diffTabRedirect}
                    onClick={onClick}
                  >
                    {hashItem.hash}
                  </Link>
                );
              }
            }
            return (
              <Link
                key={`${hashItem.url}-${hashItem.hash}`}
                href={hashItem.url || '#'}
                onClick={publish}
              >
                {hashItem.hash}
              </Link>
            );
          })
          // join React elements with commas for the copy's sake eg. "hash1, hash2, hash3"
          .reduce((prev, curr) => [prev, ', ', curr])}
      </styles.CommitText>
    </Fragment>
  );
};
